import { resolveReferencePriceDisplay } from '@/wabunka-src/common/features/Experience/models/referencePriceDisplay';
import { formatCurrency } from '@/wabunka-src/common/utils/currency';

import type { WabunkaCurrencyConvertedPrice } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_currency';
import type { DurationObject } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';

/**
 * マップピン配信レスポンス（`GET /experiences/mapPins`）の生 JSON 表現。
 *
 * バックエンドは proto JSON（protojson）で返すため、int64（price / referencePrice /
 * currencyConvertedPrice.amount）は文字列、enum（chargeType）は文字列、未設定の
 * optional フィールドは省略される。
 *
 * NOTE: wabunka-protos の pin 型が publish されるまでの暫定デコード。
 * publish 後は `WabunkaExperienceMapPinListResponse.fromJSON` へ置き換え可能。
 */
export interface WabunkaExperienceMapPinResponseJson {
  items?: WabunkaExperienceMapPinJson[];
  count?: number;
}

export interface WabunkaExperienceMapPinJson {
  id?: string;
  slug?: string;
  latitude?: number;
  longitude?: number;
  name?: string;
  thumbnailImageUrl?: string;
  chargeType?: string;
  price?: string;
  referencePrice?: string;
  isPublicPrice?: boolean;
  currencyConvertedPrice?: { amount?: string; currency?: string; rateAppliedDate?: string };
  experienceDuration?: { value?: number; unit?: string };
  area?: string;
}

/**
 * 一覧マップビューのピン＋プレビューカード表示用ビューモデル。
 * 座標は配信対象が座標を持つ体験のみのため常に有効値。
 */
export interface WabunkaExperienceMapPinViewModel {
  /** 体験ID */
  id: string;
  /** スラッグ（体験詳細ページ遷移に使用） */
  slug: string;
  /** 開催地点の緯度 */
  latitude: number;
  /** 開催地点の経度 */
  longitude: number;
  /** 体験名 */
  name: string;
  /** 代表サムネイル画像URL（無い場合は空文字） */
  thumbnailImageUrl: string;
  /** 課金タイプ（PerPersonWithMinimumGuarantee / PerGroup 等） */
  chargeType: string;
  /** 一人 or グループあたりの金額 */
  price: number;
  /** 参考料金（未設定の場合は undefined） */
  referencePrice?: number;
  /** 価格が公開か非公開か */
  isPublicPrice: boolean;
  /** 通貨換算済み価格（JPY以外の通貨が指定された場合に設定される） */
  currencyConvertedPrice?: WabunkaCurrencyConvertedPrice;
  /** 所要時間ラベル（未設定の場合は undefined） */
  experienceDuration?: DurationObject;
  /** エリア（行き先タグ名。無い場合は空文字） */
  area: string;
}

/** リスト表示用のマップピンビューモデル */
export type WabunkaExperienceMapPinListViewModel = WabunkaExperienceMapPinViewModel[];

const toNumberOrUndefined = (value: string | undefined): number | undefined => {
  if (value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * 生 JSON の1ピンをビューモデルへ変換する。
 * 緯度経度いずれかが欠落・非数値のピンは undefined を返す（マップに出さない）。
 */
const buildWabunkaExperienceMapPinViewModel = (pin: WabunkaExperienceMapPinJson): WabunkaExperienceMapPinViewModel | undefined => {
  if (typeof pin.latitude !== 'number' || typeof pin.longitude !== 'number' || !Number.isFinite(pin.latitude) || !Number.isFinite(pin.longitude)) {
    return undefined;
  }

  const currencyConvertedPrice = pin.currencyConvertedPrice
    ? {
        amount: toNumberOrUndefined(pin.currencyConvertedPrice.amount) ?? 0,
        currency: pin.currencyConvertedPrice.currency ?? '',
        rateAppliedDate: pin.currencyConvertedPrice.rateAppliedDate ?? '',
      }
    : undefined;

  const experienceDuration = pin.experienceDuration ? { value: pin.experienceDuration.value ?? 0, unit: pin.experienceDuration.unit ?? '' } : undefined;

  return {
    id: pin.id ?? '',
    slug: pin.slug ?? '',
    latitude: pin.latitude,
    longitude: pin.longitude,
    name: pin.name ?? '',
    thumbnailImageUrl: pin.thumbnailImageUrl ?? '',
    chargeType: pin.chargeType ?? '',
    price: toNumberOrUndefined(pin.price) ?? 0,
    referencePrice: toNumberOrUndefined(pin.referencePrice),
    isPublicPrice: pin.isPublicPrice ?? true,
    currencyConvertedPrice,
    experienceDuration,
    area: pin.area ?? '',
  };
};

/**
 * マップピンの参考価格ラベルを組み立てる。
 * 標準の体験カード（displayPrice / displayPriceParts）と同じ表示ロジックに揃える:
 *   - 非公開価格 → "Price upon request"
 *   - 参考料金が未設定 or 0 以下 → 空文字（価格欄を非表示）
 *   - 通貨換算価格があればそれを、無ければ JPY を "from ¥X /person" で表示
 */
export const buildMapPinPriceLabel = (pin: WabunkaExperienceMapPinViewModel): string => {
  const display = resolveReferencePriceDisplay(pin);
  const chargeLabel = ' /person';

  switch (display.kind) {
    case 'private':
      return 'Price upon request';
    case 'unpriced':
      return '';
    case 'converted':
      return `from ${formatCurrency(display.amount, display.currency)}${chargeLabel}`;
    case 'jpy':
      return `from ¥${display.amount.toLocaleString()}${chargeLabel}`;
  }
};

/**
 * マップピン配信レスポンスをリストビューモデルへ変換する。
 * 座標欠損ピンは除外する。
 */
export const buildWabunkaExperienceMapPinListViewModel = (res: WabunkaExperienceMapPinResponseJson | undefined): WabunkaExperienceMapPinListViewModel => {
  if (!res?.items) return [];
  return res.items.map(buildWabunkaExperienceMapPinViewModel).filter((pin): pin is WabunkaExperienceMapPinViewModel => pin !== undefined);
};
